import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2 } from '@digital-twins/design-system/icons'

import { FailedExhibit } from '@/features/studies/report/reportViewState'

export interface FailedSlidePlaceholderProps {
  exhibit: FailedExhibit
  /** A public share-link view: never offers Retry. */
  isPublicView: boolean
  /** Regenerates the report (a section retry of the failed exhibits when possible). */
  onRetry?: () => void
  /** A retry is in flight. */
  isRetrying?: boolean
}

/**
 * A plain slide standing in for an exhibit that could not be produced, at
 * its place in the deck. Shows the typed error code, the slide id and the
 * attempt id so QA can trace it; the codes are identifiers, not copy. The
 * report owner gets the same Retry the page banner offers.
 */
export function FailedSlidePlaceholder(props: FailedSlidePlaceholderProps) {
  const { exhibit, isPublicView, onRetry, isRetrying = false } = props

  const { t } = useTranslation('app')

  const unknown = t('studyReport.failedSlide.unknownValue')
  const rows: Array<{ key: string; label: string; value: string }> = [
    {
      key: 'code',
      label: t('studyReport.failedSlide.codeLabel'),
      value: exhibit.failureReason ?? unknown,
    },
    {
      key: 'slide',
      label: t('studyReport.failedSlide.slideLabel'),
      value: exhibit.slideId,
    },
    {
      key: 'attempt',
      label: t('studyReport.failedSlide.attemptLabel'),
      value: exhibit.attemptId ?? unknown,
    },
  ]

  return (
    <div
      data-testid="deck-failed-slide-placeholder"
      data-slide-id={exhibit.slideId}
      className="flex h-full w-full flex-col items-center justify-center gap-10 bg-muted px-24 text-center"
    >
      <div className="flex flex-col items-center gap-6">
        <AlertCircle className="h-16 w-16 text-destructive" strokeWidth={1.5} />
        <h2 className="text-5xl font-semibold text-foreground">
          {t('studyReport.failedSlide.title')}
        </h2>
        <p className="text-2xl text-muted-foreground">{t('studyReport.failedSlide.body')}</p>
      </div>
      <dl className="grid grid-cols-[auto_auto] gap-x-8 gap-y-3 text-left text-2xl">
        {rows.map((row) => (
          <div key={row.key} className="contents">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd
              data-testid={`deck-failed-slide-${row.key}`}
              className="font-mono text-foreground"
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      {onRetry && !isPublicView && (
        <button
          type="button"
          data-testid="deck-failed-slide-retry-button"
          onClick={onRetry}
          disabled={isRetrying}
          className="flex items-center gap-3 rounded-full bg-primary px-8 py-3 text-2xl font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {isRetrying && <Loader2 className="h-6 w-6 animate-spin" />}
          {isRetrying ? t('studyReport.retryingGeneration') : t('studyReport.retryGeneration')}
        </button>
      )}
    </div>
  )
}
